import {
  Activity,
  ArrowLeftRight,
  Database,
  Percent,
  TrendingUp,
  Users,
  type LucideIcon,
} from "lucide-react";

export type McpSource = "etherscan" | "dune";

export type McpCadenceTone = "muted" | "live" | "daily" | "monthly";

export interface McpDataset {
  slug: string;
  name: string;
  description: string;
  icon: LucideIcon;
  cadence: string;
  cadenceTone: McpCadenceTone;
  requiredParam?: string;
  paramNote?: string;
  queryOptions: string;
}

export interface McpSourceGroup {
  source: McpSource;
  label: string;
  tagline: string;
  description: string;
  datasets: McpDataset[];
}

export const mcpSourceGroups: McpSourceGroup[] = [
  {
    source: "etherscan",
    label: "Etherscan",
    tagline: "Direct chain reads & persisted snapshots",
    description:
      "Read straight off contract state and price feeds, then persisted on a schedule so history is queryable.",
    datasets: [
      {
        slug: "api:tokens",
        name: "Token Registry",
        description:
          "All configured ERC-20 tokens tracked by Chain Fundamentals — name, contract address, decimal divisor, chain ID, and token class (stablecoin, governance, yieldcoin, erc4626, morphovault).",
        icon: Database,
        cadence: "Static reference",
        cadenceTone: "muted",
        queryOptions: "No date range or pagination",
      },
      {
        slug: "api:market-token-prices",
        name: "Market Prices — Latest",
        description:
          "Latest USD price and 24h / 30d / 1y performance for SKY, AAVE, MORPHO and ENA.",
        icon: TrendingUp,
        cadence: "Live · ~5 min",
        cadenceTone: "live",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "api:market-token-price-history",
        name: "Market Price History",
        description:
          "Full USD price history for a single asset, with the same 24h / 30d / 1y performance figures at each snapshot.",
        icon: TrendingUp,
        cadence: "Live · ~5 min",
        cadenceTone: "live",
        requiredParam: "asset*",
        paramNote: "— SKY, AAVE, MORPHO, or ENA",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "api:savings-tokens-apy",
        name: "Savings APY — Latest",
        description:
          "Latest APY snapshot across every savings token — 1d / 7d / 30d / 90d / 365d trailing windows, TVL, total supply and exchange rate.",
        icon: Percent,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "api:savings-token-apy-history",
        name: "Savings APY History",
        description:
          "Historical current-APY series for one savings token, as listed by the Savings APY dataset above.",
        icon: Percent,
        cadence: "Daily",
        cadenceTone: "daily",
        requiredParam: "symbol*",
        paramNote: "— e.g. sUSDS",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "api:token-holders",
        name: "Token Holders",
        description:
          "Latest holder balances and rank for a tracked token, by wallet address.",
        icon: Users,
        cadence: "Daily",
        cadenceTone: "daily",
        requiredParam: "token_name*",
        paramNote: "· chain_id (default 1)",
        queryOptions: "Pagination only",
      },
      {
        slug: "api:token-holdings-by-tag",
        name: "Holdings by Tag — Latest",
        description:
          "Latest holdings for a token grouped into labeled cohorts — vaults, exchanges, foundations, whales — with residual supply folded into \u201cOther\u201d.",
        icon: Users,
        cadence: "Daily",
        cadenceTone: "daily",
        requiredParam: "token_name*",
        paramNote: "· chain_id (default 1)",
        queryOptions: "No date range or pagination",
      },
      {
        slug: "api:token-holdings-by-tag-historical",
        name: "Holdings by Tag — Historical",
        description:
          "Day-by-day history of the tagged holdings breakdown above, one snapshot per day, for trending cohort balances over time.",
        icon: Users,
        cadence: "Daily",
        cadenceTone: "daily",
        requiredParam: "token_name*",
        paramNote: "· chain_id (default 1)",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "api:token-total-supply-history",
        name: "Total Supply History",
        description: "Historical total supply snapshots for a tracked token.",
        icon: TrendingUp,
        cadence: "Daily",
        cadenceTone: "daily",
        requiredParam: "token_name*",
        paramNote: "· chain_id (default 1)",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "api:protocols-tvl",
        name: "Protocol Stablecoin TVL",
        description:
          "30-day trailing stablecoin supply and borrow across Aave v3, Compound v3, Curve LlamaLend, Liquity v2, Morpho and SparkLend. Sourced from Sphere.",
        icon: TrendingUp,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "api:stablecoin-market-comparison",
        name: "Stablecoin Market Comparison",
        description:
          "Historical supply and TVL side by side for select stablecoins and protocols — a quick view of USDS against the broader market.",
        icon: TrendingUp,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
    ],
  },
  {
    source: "dune",
    label: "Dune",
    tagline: "Curated SQL analytics",
    description:
      "Purpose-built Dune queries — currently centered on the Sky Ecosystem, our largest protocol coverage today, spanning treasury flows, restaking, and per-agent transaction ledgers. More protocols land here as coverage expands.",
    datasets: [
      {
        slug: "dune:5376479",
        name: "SPK Farming Demand (USDS)",
        description:
          "USDS demand from the SPK token rewards farm — deposit balances by wallet cohort over time.",
        icon: Activity,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:6138270",
        name: "Lockstake Restaking Analysis",
        description:
          "Deposit-SKY-earn-SKY farm activity — claims, locked/freed balances, and restaking index over time.",
        icon: Activity,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:6158493",
        name: "Lockstake Depositors Over Time",
        description:
          "SKY deposited into the Lockstake farm, broken out by wallet cohort, tracked day by day.",
        icon: Activity,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:6314629",
        name: "Capital Buffer — Gross Inflows",
        description:
          "Gross cash inflows to Sky's capital buffer, by category — yield, buybacks, delegate compensation and more.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:6323958",
        name: "Capital Buffer — Gross Outflows",
        description:
          "Gross cash outflows from Sky's capital buffer, by category — liquidations, RWA costs, vault expenses and more.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:6777432",
        name: "Morpho Curation Deposits",
        description:
          "Stablecoin deposit balances into Sky-curated Morpho vaults, by vault and deposit token.",
        icon: Activity,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:6808860",
        name: "Sky Frontier Foundation Holdings",
        description:
          "Sky Frontier Foundation's stablecoin holdings and Lockstake borrowing position, tracked daily.",
        icon: Users,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:6854509",
        name: "Lockstake Yield Rate",
        description:
          "The yield rate the protocol pays on the deposit-SKY-earn-SKY Lockstake farm over time.",
        icon: Percent,
        cadence: "Daily",
        cadenceTone: "daily",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:7444703",
        name: "Sky Frontier Foundation Transactions",
        description:
          "Sky Frontier Foundation's stablecoin transactions, grouped by month and counterparty.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:7507144",
        name: "Core Council Transactions",
        description:
          "Core Council's stablecoin transactions, grouped by month and counterparty.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:7637934",
        name: "Sky Agents — Obex Transactions",
        description:
          "Stablecoin transactions for the Obex Sky Agent, grouped by month and counterparty.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:7637961",
        name: "Sky Agents — Grove Transactions",
        description:
          "Stablecoin transactions for the Grove Sky Agent, grouped by month and counterparty.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:7637968",
        name: "Sky Agents — Keel Transactions",
        description:
          "Stablecoin transactions for the Keel Sky Agent, grouped by month and counterparty.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:7646158",
        name: "Sky Agents — Spark Transactions",
        description:
          "Stablecoin transactions for the Spark Sky Agent, grouped by month and counterparty.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
      {
        slug: "dune:7661343",
        name: "Sky Agents — Skybase Transactions",
        description:
          "Stablecoin transactions for the Skybase Sky Agent, grouped by month and counterparty.",
        icon: ArrowLeftRight,
        cadence: "Daily · monthly rows",
        cadenceTone: "monthly",
        queryOptions: "Date range, pagination",
      },
    ],
  },
];
